import L, {
  type DivIconOptions,
  type IconOptions,
  type Layer,
  type Map as LeafletMap,
  type PathOptions,
  type PointExpression,
} from "leaflet";
import type { PrintLayerAdapter } from "./printLayerAdapterRegistry";
import { MAP_EXPORT_PARCEL_POLYGON_CLASS } from "../constants/mapExport";
import { CURRENT_LOCATION_PANE_NAME } from "../constants/pane";
import { CanvasPinMarker } from "./canvasPin/CanvasPinMarker";
export const DRAWN_SHAPES_PANE_NAME = "drawnShapesPane";

/**
 * 地番ポリゴン（選択中の物件・クリックした地番の強調表示）かどうか。
 * overlayPane のデフォルトレンダラーに乗るため drawnShapesPane では
 * 判定できず、出力用マーカークラスで識別する。
 * react-leaflet 経由ではクラスは options ではなく DOM 要素にのみ付与される
 * （ParcelPolygon が add イベントで直接付与する）ため、要素側も確認する。
 */
const isParcelPolygonPath = (path: L.Path): boolean => {
  if (
    path.getElement()?.classList.contains(MAP_EXPORT_PARCEL_POLYGON_CLASS) ===
    true
  ) {
    return true;
  }
  const className = path.options.className;
  return (
    typeof className === "string" &&
    className.split(/\s+/).includes(MAP_EXPORT_PARCEL_POLYGON_CLASS)
  );
};

export interface LeafletPrintAdapterOptions {
  map: LeafletMap;
  includePins: boolean;
  includeDrawnShapes: boolean;
  drawnShapesPaneName: string;
}

type PointOptionName =
  | "iconSize"
  | "iconAnchor"
  | "popupAnchor"
  | "tooltipAnchor"
  | "shadowSize"
  | "shadowAnchor";

type PathStyleName = "weight" | "dashArray" | "dashOffset";

interface MarkerSnapshot {
  marker: L.Marker;
  icon: L.Icon | L.DivIcon;
  iconClass: string | null | undefined;
  shadowClass: string | null | undefined;
  touched: boolean;
}

interface CanvasPinSnapshot {
  pin: CanvasPinMarker;
  scale: number;
  touched: boolean;
}

interface OptionSnapshot {
  present: boolean;
  value: unknown;
}

interface PathSnapshot {
  path: L.Path;
  options: Record<PathStyleName, OptionSnapshot>;
  radius?: {
    value: number;
    option: OptionSnapshot;
  };
  touched: boolean;
  radiusTouched: boolean;
}

type IconConstructor = new (options?: IconOptions & DivIconOptions) =>
  | L.Icon
  | L.DivIcon;

const pointOptionNames: PointOptionName[] = [
  "iconSize",
  "iconAnchor",
  "popupAnchor",
  "tooltipAnchor",
  "shadowSize",
  "shadowAnchor",
];

const pathStyleNames: PathStyleName[] = ["weight", "dashArray", "dashOffset"];

const copyEnumerableOptions = <T extends object>(options: T): T => {
  const copy: Record<string, unknown> = {};

  for (const name in options) {
    copy[name] = options[name];
  }

  return copy as T;
};

const scalePoint = (
  value: PointExpression | undefined,
  printScale: number
): PointExpression | undefined => {
  if (value == null) return value;

  if (Array.isArray(value)) {
    return [value[0] * printScale, value[1] * printScale];
  }

  return L.point(value.x * printScale, value.y * printScale);
};

const createScaledDivIconHtml = (
  html: DivIconOptions["html"],
  printScale: number
): HTMLElement | string => {
  const style = `transform: scale(${printScale}); transform-origin: top left;`;

  if (typeof html === "string") {
    return `<div data-map-print-icon-content style="${style}">${html}</div>`;
  }

  const wrapper = document.createElement("div");
  wrapper.dataset.mapPrintIconContent = "";
  wrapper.style.transform = `scale(${printScale})`;
  wrapper.style.transformOrigin = "top left";

  if (html instanceof HTMLElement) {
    wrapper.append(html.cloneNode(true));
  }

  return wrapper;
};

const createScaledIcon = (
  icon: L.Icon | L.DivIcon,
  printScale: number
): L.Icon | L.DivIcon => {
  const options = copyEnumerableOptions(icon.options) as IconOptions &
    DivIconOptions;

  for (const name of pointOptionNames) {
    const value = options[name];
    if (value !== undefined) {
      options[name] = scalePoint(value, printScale);
    }
  }

  if (icon instanceof L.DivIcon) {
    options.html = createScaledDivIconHtml(icon.options.html, printScale);
  }

  const IconClass = icon.constructor as IconConstructor;
  return new IconClass(options);
};

const getMarkerShadow = (marker: L.Marker): HTMLElement | null =>
  (marker as unknown as { _shadow: HTMLElement | null })._shadow;

const snapshotClass = (
  element: HTMLElement | null | undefined
): string | null | undefined =>
  element == null ? undefined : element.getAttribute("class");

const restoreClass = (
  element: HTMLElement | null | undefined,
  className: string | null | undefined
): void => {
  if (element == null || className === undefined) return;

  if (className === null) {
    element.removeAttribute("class");
  } else {
    element.setAttribute("class", className);
  }
};

const restoreMarkerClasses = (snapshot: MarkerSnapshot): void => {
  restoreClass(snapshot.marker.getElement(), snapshot.iconClass);
  restoreClass(getMarkerShadow(snapshot.marker), snapshot.shadowClass);
};

const getCandidateLayers = (map: LeafletMap): Set<Layer> => {
  const candidates = new Set<Layer>();

  const visit = (layer: Layer): void => {
    if (candidates.has(layer)) return;

    candidates.add(layer);
    if (layer instanceof L.LayerGroup) {
      layer.eachLayer(visit);
    }
  };

  map.eachLayer(visit);
  return new Set([...candidates].filter((layer) => map.hasLayer(layer)));
};

const snapshotOption = (options: object, name: string): OptionSnapshot => ({
  present: Object.prototype.hasOwnProperty.call(options, name),
  value: (options as unknown as Record<string, unknown>)[name],
});

const scaleDashArray = (
  value: unknown,
  printScale: number
): PathOptions["dashArray"] => {
  if (Array.isArray(value)) {
    return value.every(
      (part): part is number =>
        typeof part === "number" && Number.isFinite(part)
    )
      ? value.map((part) => part * printScale)
      : (value as PathOptions["dashArray"]);
  }

  if (typeof value !== "string") {
    return value as PathOptions["dashArray"];
  }

  const parts = value.split(/([,\s]+)/);
  const values = parts.filter((part) => !/^[,\s]+$/.test(part));
  if (
    values.length === 0 ||
    values.some((part) => part === "" || !Number.isFinite(Number(part)))
  ) {
    return value;
  }

  return parts
    .map((part) =>
      /^[,\s]+$/.test(part) ? part : String(Number(part) * printScale)
    )
    .join("");
};

const scaleDashOffset = (
  value: unknown,
  printScale: number
): PathOptions["dashOffset"] => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return (value * printScale) as unknown as string;
  }
  if (
    typeof value === "string" &&
    value.trim() !== "" &&
    Number.isFinite(Number(value))
  ) {
    return String(Number(value) * printScale);
  }
  return value as PathOptions["dashOffset"];
};

const getScaledPathStyle = (
  path: PathSnapshot,
  printScale: number
): PathOptions => {
  const style: PathOptions = {};
  const weight = path.options.weight.value;
  const dashArray = path.options.dashArray.value;
  const dashOffset = path.options.dashOffset.value;

  if (typeof weight === "number" && Number.isFinite(weight)) {
    style.weight = weight * printScale;
  }

  const scaledDashArray = scaleDashArray(dashArray, printScale);
  if (scaledDashArray !== undefined) style.dashArray = scaledDashArray;

  const scaledDashOffset = scaleDashOffset(dashOffset, printScale);
  if (scaledDashOffset !== undefined) style.dashOffset = scaledDashOffset;

  return style;
};

const setPathStyleWithoutChangingRadius = (
  path: L.Path,
  style: PathOptions
): void => {
  if (path instanceof L.CircleMarker && !(path instanceof L.Circle)) {
    L.Path.prototype.setStyle.call(path, style);
    return;
  }

  path.setStyle(style);
};

const restoreOptionPresence = (
  options: object,
  name: string,
  snapshot: OptionSnapshot
): void => {
  if (snapshot.present) {
    (options as unknown as Record<string, unknown>)[name] = snapshot.value;
  } else {
    Reflect.deleteProperty(options, name);
  }
};

export const createLeafletPrintAdapter = ({
  map,
  includePins,
  includeDrawnShapes,
  drawnShapesPaneName,
}: LeafletPrintAdapterOptions): PrintLayerAdapter => {
  const candidates = getCandidateLayers(map);
  const markers: MarkerSnapshot[] = includePins
    ? [...candidates]
        .filter((layer): layer is L.Marker => layer instanceof L.Marker)
        .flatMap((marker) => {
          const icon = marker.options.icon;
          return icon == null
            ? []
            : [
                {
                  marker,
                  icon,
                  iconClass: snapshotClass(marker.getElement()),
                  shadowClass: snapshotClass(getMarkerShadow(marker)),
                  touched: false,
                },
              ];
        })
    : [];
  // Canvas に描くピン（CanvasPinMarker）は L.Marker ではないため、印刷倍率を別途渡して拡大する
  const canvasPins: CanvasPinSnapshot[] = includePins
    ? [...candidates]
        .filter(
          (layer): layer is CanvasPinMarker => layer instanceof CanvasPinMarker
        )
        .map((pin) => ({ pin, scale: pin.getPrintScale(), touched: false }))
    : [];
  // 描画図形は includeDrawnShapes に従い、地番ポリゴンや現在地ペイン上の図形は
  // 出力に常に含まれる（mapExportVisibility 参照）ため無条件にスケーリング対象へ含める。
  const paths: PathSnapshot[] = [...candidates]
    .filter(
      (layer): layer is L.Path =>
        layer instanceof L.Path &&
        ((includeDrawnShapes && layer.options.pane === drawnShapesPaneName) ||
          isParcelPolygonPath(layer) ||
          layer.options.pane === CURRENT_LOCATION_PANE_NAME ||
          layer.options.className === "map-export-current-location-marker")
    )
    .map((path) => ({
      path,
      options: {
        weight: snapshotOption(path.options, "weight"),
        dashArray: snapshotOption(path.options, "dashArray"),
        dashOffset: snapshotOption(path.options, "dashOffset"),
      },
      radius:
        path instanceof L.CircleMarker && !(path instanceof L.Circle)
          ? {
              value: path.getRadius(),
              option: snapshotOption(path.options, "radius"),
            }
          : undefined,
      touched: false,
      radiusTouched: false,
    }));
  let restored = false;

  return {
    id: "leaflet-overlays",
    apply(context) {
      for (const snapshot of markers) {
        if (!map.hasLayer(snapshot.marker)) continue;
        if (context.printScale === 1) continue;

        snapshot.touched = true;
        try {
          snapshot.marker.setIcon(
            createScaledIcon(snapshot.icon, context.printScale)
          );
        } finally {
          restoreMarkerClasses(snapshot);
        }
      }

      for (const snapshot of canvasPins) {
        if (!map.hasLayer(snapshot.pin)) continue;
        if (context.printScale === 1) continue;

        snapshot.touched = true;
        snapshot.pin.setPrintScale(context.printScale);
      }

      for (const snapshot of paths) {
        if (!map.hasLayer(snapshot.path)) continue;

        let pathScale = context.printScale;
        // 現在地マーカーは 300dpi（printScale=1）のときでもキャンバスの拡大に追従できるよう、
        // 画面と300dpiキャンバスの比率（約3倍）をベーススケールとして追加で掛ける。
        // これにより、DPIに関係なく紙の上で一定の物理的大きさになる。
        if (
          snapshot.path.options.className ===
          "map-export-current-location-marker"
        ) {
          pathScale = context.printScale * 3;
        }

        if (pathScale === 1) continue;

        snapshot.touched = true;
        if (snapshot.radius !== undefined) snapshot.radiusTouched = true;
        setPathStyleWithoutChangingRadius(
          snapshot.path,
          getScaledPathStyle(snapshot, pathScale)
        );

        if (
          snapshot.radius !== undefined &&
          snapshot.path instanceof L.CircleMarker
        ) {
          snapshot.path.setRadius(snapshot.radius.value * pathScale);
        }
      }
    },
    restore() {
      if (restored) return;
      restored = true;

      const errors: unknown[] = [];

      for (const snapshot of markers) {
        if (!snapshot.touched) continue;
        try {
          snapshot.marker.setIcon(snapshot.icon);
        } catch (error) {
          errors.push(error);
        } finally {
          snapshot.marker.options.icon = snapshot.icon;
          restoreMarkerClasses(snapshot);
        }
      }

      for (const snapshot of canvasPins) {
        if (!snapshot.touched) continue;
        try {
          snapshot.pin.setPrintScale(snapshot.scale);
        } catch (error) {
          errors.push(error);
        }
      }

      for (const snapshot of paths) {
        if (!snapshot.touched) continue;

        try {
          setPathStyleWithoutChangingRadius(snapshot.path, {
            weight: snapshot.options.weight.value as number | undefined,
            dashArray: snapshot.options.dashArray.value as
              | string
              | number[]
              | undefined,
            dashOffset: snapshot.options.dashOffset.value as string | undefined,
          });
        } catch (error) {
          errors.push(error);
        } finally {
          for (const name of pathStyleNames) {
            restoreOptionPresence(
              snapshot.path.options,
              name,
              snapshot.options[name]
            );
          }
        }

        if (
          snapshot.radiusTouched &&
          snapshot.radius !== undefined &&
          snapshot.path instanceof L.CircleMarker
        ) {
          try {
            snapshot.path.setRadius(snapshot.radius.value);
          } catch (error) {
            errors.push(error);
          } finally {
            restoreOptionPresence(
              snapshot.path.options,
              "radius",
              snapshot.radius.option
            );
            (
              snapshot.path as unknown as {
                _radius: number;
              }
            )._radius = snapshot.radius.value;
          }
        }
      }

      if (errors.length > 0) {
        throw new AggregateError(errors, "Failed to restore Leaflet overlays");
      }
    },
  };
};
